import { useRef } from 'react'
import { useExportChartImage } from '../../../hooks/useExportChartImage'
import type ReactECharts from 'echarts-for-react'
import { RiskProfileCard } from '../../RiskProfileCard'
import { ChartQueryState } from '../../ChartQueryState'
import { ChartExportActions } from '../../../chart/ChartExportActions'
import { useExportXlsx } from '../../../hooks/useExportXlsx'
import type { RiskProfileQueryParams } from '../../../hooks/useRiskProfileQueryParams'
import type { RiskProfileMetricOption } from '../../../types/riskProfile.types'
import { useProfileAreaData } from '../../../hooks/useProfileAreaData'
import RiskByAreaChart from '../../../chart/RiskByArea.charts'

export interface RiskByAreaWidgetProps {
  queryParams: RiskProfileQueryParams | null
  metricOption: RiskProfileMetricOption
  areas: string[]
  selectedAreaTypesLabel: string
}

export function RiskByAreaWidget({
  queryParams,
  metricOption,
  areas,
  selectedAreaTypesLabel,
}: RiskByAreaWidgetProps) {
  const { request, query } = useProfileAreaData(queryParams)
  const { exportXlsx, isExporting } = useExportXlsx(request)
  const chartRef = useRef<ReactECharts>(null)
  const handleExportImage = useExportChartImage(chartRef, 'risk-by-area')

  return (
    <RiskProfileCard
      title={`${selectedAreaTypesLabel} ranked by ${metricOption.label.toLocaleLowerCase()}`}
      description={`${selectedAreaTypesLabel} ranked by ${metricOption.label.toLocaleLowerCase()}. Shows where risk is concentrated to help identify higher priority areas.`}
      onExport={
        <ChartExportActions
          onExportXlsx={exportXlsx}
          onExportImage={handleExportImage}
          isExportingXlsx={isExporting}
          xlsxDisabled={!request}
        />
      }
      className="min-h-96"
    >
      <ChartQueryState query={query}>
        {(data) => (
          <RiskByAreaChart
            ref={chartRef}
            data={data}
            metricOption={metricOption}
            selectedAreas={areas}
          />
        )}
      </ChartQueryState>
    </RiskProfileCard>
  )
}
